import NodeSet from "./NodeSet";
import {arrayRemove, insertUniq, isArray} from "../voidash";

const INIT_VALUE = {}; // unique value
let nextID = 1;

export default class Node {
  constructor(graph, pred = []) {
    this.graph = graph;
    this.id = nextID++;
    this.label = "";
    this.value = INIT_VALUE;
    this.pred = pred;
    this.deps = [];
    this.succ = new NodeSet();
    this.active = true;
    this.observing = false;
    this.auxSucc = 0;
    this.graph.notifyCreate(this);
  }

  lbl(label) {
    this.label = label;
    return this;
  }

  get() {
    this.incAuxSucc();
    this.graph.update();
    this.decAuxSucc();
    return this.value;
  }

  read(cb) {
    const observer = this.o(val => {
      observer.deactivate();
      cb(val);
    });
    return observer;
  }

  update() {}

  o(fn) { return this.graph.o([this], fn); }
  t(fn) { return this.graph.t([this], fn); }

  oDelta(prev, fn) {
    return this.delta(prev, fn, "o");
  }

  tDelta(prev, fn) {
    return this.delta(prev, fn, "t");
  }

  delta(prev, fn, method) {
    return this[method](val => {
      const res = fn(val, prev);
      prev = val;
      return res;
    });
  }

  oReduce(prev, fn) {
    return this.reduce(prev, fn, "o");
  }

  tReduce(prev, fn) {
    return this.reduce(prev, fn, "t");
  }

  reduce(prev, fn, method) {
    return this[method](val => prev = fn(val, prev));
  }

  pipe(sourceNode) {
    const node = this.o(val => sourceNode.set(val)).lbl("pipe");
    sourceNode.addPredecessor(node);
    return node;
  }

  addPredecessor(node) {
    this.pred.push(node);
    if (this.active) node.addSuccessor(this);
  }

  removePredecessor(node) {
    return arrayRemove(this.pred, node);
  }

  forEachPredecessor(fn) {
    this.pred.forEach(fn);
  }

  addSuccessor(node) {
    this.handleSuccInc();
    this.succ.add(node);
  }

  removeSuccessor(node) {
    this.succ.remove(node);
    this.handleSuccDec();
  }

  handleSuccInc() {}

  handleSuccDec() {}

  forEachSuccessor(fn) {
    this.succ.forEach(fn);
  }

  depend(nodes) {
    if (isArray(nodes)) nodes.forEach(n => this.depend(n));
    else insertUniq(this.deps, nodes);
    return this;
  }

  undepend(nodes) {
    if (isArray(nodes)) nodes.forEach(n => this.undepend(n));
    else arrayRemove(this.deps, nodes);
    return this;
  }

  forEachDependence(fn) {
    this.pred.forEach(fn);
    this.deps.forEach(fn);
  }

  notifyChange() {
    this.graph.notifyChange(this);
  }

  isSource() {
    return this.pred.length === 0;
  }

  isSink() {
    return this.succ.size() + this.auxSucc === 0;
  }

  incAuxSucc() {
    this.handleSuccInc();
    this.auxSucc++;
  }

  decAuxSucc() {
    this.auxSucc--;
    this.handleSuccDec();
  }

  deactivate() {
    this.active = false;
    this.stopObserving();
  }

  activate() {
    this.active = true;
    this.startObserving();
  }

  stopObserving() {
    this.pred.forEach(n => n.removeSuccessor(this));
    this.observing = false;
  }

  startObserving() {
    this.observing = true;
    this.pred.forEach(n => { n.addSuccessor(this); });
    if (this.pred.length !== 0) this.notifyChange();
  }
}
